/**
 * Table Widget Component
 * Displays data in a sortable, filterable table
 * Supports Excel-style conditional formatting and column formatting
 * Supports Expandable Rows for nested data (e.g., OrderLines from $expand)
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronRightIcon,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  ArrowUp,
  ArrowDown,
  Minus,
  Star,
  Flag,
  Circle,
  X,
  Filter,
  FilterX
} from 'lucide-react';
import {
  TableData,
  TableColumn,
  ColumnFormat,
  ConditionalFormat,
  ConditionalFormatStyle,
  ColumnFilters,
  ColumnFilter,
  FilterOperator,
  FILTER_OPERATOR_LABELS,
  DEFAULT_OPERATORS_BY_TYPE
} from '../../types';
import clsx from 'clsx';

// Configuration for expandable rows (nested data like OrderLines)
export interface ExpandableConfig {
  enabled: boolean;
  // Field that contains the nested array (e.g., "OrderLines", "Operations")
  dataField: string;
  // Columns to display in the expanded sub-table
  columns: TableColumn[];
  // Optional: Title for the expanded section
  title?: string;
  // Optional: Show row count badge
  showCount?: boolean;
}

interface TableWidgetProps {
  data: TableData;
  pageSize?: number;
  showPagination?: boolean;
  striped?: boolean;
  loading?: boolean;
  onRowClick?: (row: Record<string, unknown>) => void;
  // NEW: Expandable rows configuration
  expandable?: ExpandableConfig;
  // NEW: Column filter configuration (Incremental Search like Infor LN)
  showColumnFilters?: boolean;
  onFilterChange?: (filters: ColumnFilters) => void;
  // Debounce delay in ms for filter input (default: 300)
  filterDebounceMs?: number;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  column: string | null;
  direction: SortDirection;
}

// Icon mapping for conditional formatting
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'info': Info,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'minus': Minus,
  'star': Star,
  'flag': Flag,
  'circle': Circle,
};

export function TableWidget({
  data,
  pageSize = 20,
  showPagination = true,
  striped = true,
  loading,
  onRowClick,
  expandable,
  showColumnFilters = false,
  onFilterChange,
  filterDebounceMs = 300,
}: TableWidgetProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  // Track which rows are expanded (by row index in paginated data)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // NEW: Column filters state (Incremental Search)
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const [filterInputs, setFilterInputs] = useState<Record<string, string>>({});
  const filterTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(columnFilters).some(f => f.value.trim() !== '');
  }, [columnFilters]);

  // Get default operator for a column based on its format type
  const getDefaultOperator = useCallback((col: TableColumn): FilterOperator => {
    const type = col.format?.type || 'text';
    return DEFAULT_OPERATORS_BY_TYPE[type] || 'contains';
  }, []);

  // Handle filter input change with debounce
  const handleFilterInputChange = useCallback((field: string, value: string, col: TableColumn) => {
    // Update input immediately for responsive UI
    setFilterInputs(prev => ({ ...prev, [field]: value }));

    // Clear existing timeout for this field
    if (filterTimeoutRef.current[field]) {
      clearTimeout(filterTimeoutRef.current[field]);
    }

    // Debounce the actual filter application
    filterTimeoutRef.current[field] = setTimeout(() => {
      const operator = columnFilters[field]?.operator || getDefaultOperator(col);
      const newFilters = { ...columnFilters };

      if (value.trim() === '') {
        delete newFilters[field];
      } else {
        newFilters[field] = { value: value.trim(), operator };
      }

      setColumnFilters(newFilters);
      onFilterChange?.(newFilters);
      // Reset to first page when filters change
      setCurrentPage(1);
    }, filterDebounceMs);
  }, [columnFilters, filterDebounceMs, getDefaultOperator, onFilterChange]);

  // Handle operator change for a column
  const handleOperatorChange = useCallback((field: string, operator: FilterOperator) => {
    const currentValue = columnFilters[field]?.value || filterInputs[field] || '';

    if (currentValue.trim() === '') {
      // Just update the operator for future input
      setColumnFilters(prev => ({
        ...prev,
        [field]: { value: '', operator }
      }));
    } else {
      // Re-apply filter with new operator
      const newFilters = {
        ...columnFilters,
        [field]: { value: currentValue.trim(), operator }
      };
      setColumnFilters(newFilters);
      onFilterChange?.(newFilters);
      setCurrentPage(1);
    }
  }, [columnFilters, filterInputs, onFilterChange]);

  // Clear single filter
  const clearFilter = useCallback((field: string) => {
    setFilterInputs(prev => ({ ...prev, [field]: '' }));
    const newFilters = { ...columnFilters };
    delete newFilters[field];
    setColumnFilters(newFilters);
    onFilterChange?.(newFilters);
  }, [columnFilters, onFilterChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilterInputs({});
    setColumnFilters({});
    onFilterChange?.({});
    setCurrentPage(1);
  }, [onFilterChange]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(filterTimeoutRef.current).forEach(clearTimeout);
    };
  }, []);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((rowIdx: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger onRowClick
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIdx)) {
        next.delete(rowIdx);
      } else {
        next.add(rowIdx);
      }
      return next;
    });
  }, []);

  // Check if a row has expandable data
  const hasExpandableData = useCallback((row: Record<string, unknown>): boolean => {
    if (!expandable?.enabled || !expandable.dataField) return false;
    const nestedData = row[expandable.dataField];
    return Array.isArray(nestedData) && nestedData.length > 0;
  }, [expandable]);

  // Get nested data count for a row
  const getNestedDataCount = useCallback((row: Record<string, unknown>): number => {
    if (!expandable?.dataField) return 0;
    const nestedData = row[expandable.dataField];
    return Array.isArray(nestedData) ? nestedData.length : 0;
  }, [expandable]);

  // Apply column filters to data
  const filteredRows = useMemo(() => {
    if (!hasActiveFilters) {
      return data.rows;
    }

    return data.rows.filter(row => {
      // Row must match ALL active filters (AND logic)
      return Object.entries(columnFilters).every(([field, filter]) => {
        if (!filter.value.trim()) return true;

        const cellValue = row[field];
        const filterValue = filter.value.toLowerCase();

        // Handle null/undefined
        if (cellValue === null || cellValue === undefined) {
          return filter.operator === 'not_equals';
        }

        const stringValue = String(cellValue).toLowerCase();
        const numericCellValue = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
        const numericFilterValue = parseFloat(filter.value);

        switch (filter.operator) {
          case 'contains':
            return stringValue.includes(filterValue);

          case 'starts_with':
            return stringValue.startsWith(filterValue);

          case 'equals':
            // Try numeric comparison first, then string
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue === numericFilterValue;
            }
            return stringValue === filterValue;

          case 'not_equals':
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue !== numericFilterValue;
            }
            return stringValue !== filterValue;

          case 'greater':
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue > numericFilterValue;
            }
            return stringValue > filterValue;

          case 'greater_equal':
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue >= numericFilterValue;
            }
            return stringValue >= filterValue;

          case 'less':
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue < numericFilterValue;
            }
            return stringValue < filterValue;

          case 'less_equal':
            if (!isNaN(numericCellValue) && !isNaN(numericFilterValue)) {
              return numericCellValue <= numericFilterValue;
            }
            return stringValue <= filterValue;

          default:
            return true;
        }
      });
    });
  }, [data.rows, columnFilters, hasActiveFilters]);

  // Sort data (now uses filteredRows)
  const sortedRows = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return filteredRows;
    }

    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortState.column!];
      const bVal = b[sortState.column!];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortState.direction === 'desc' ? -comparison : comparison;
    });
  }, [filteredRows, sortState]);

  // Paginate data
  const paginatedRows = useMemo(() => {
    if (!showPagination) return sortedRows;
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize, showPagination]);

  const totalPages = Math.ceil(sortedRows.length / pageSize);

  // Handle sort
  const handleSort = (column: string) => {
    setSortState((prev) => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return { column: null, direction: null };
    });
  };

  // Render sort icon
  const SortIcon = ({ column }: { column: string }) => {
    if (sortState.column !== column) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    if (sortState.direction === 'asc') {
      return <ChevronUp className="w-4 h-4 text-[#45bdd6]" />;
    }
    return <ChevronDown className="w-4 h-4 text-[#45bdd6]" />;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-10 bg-gray-200 rounded" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data.columns.length || !data.rows.length) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  // Calculate total columns including expand column
  const totalColumns = expandable?.enabled ? data.columns.length + 1 : data.columns.length;

  return (
    <div className="flex flex-col h-full">
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {/* Expand column header */}
              {expandable?.enabled && (
                <th className="w-10 px-2 py-3 border-b border-gray-200 bg-gray-50 text-gray-400 text-xs text-center">
                  {/* Empty - expand buttons show in rows */}
                </th>
              )}
              {data.columns.map((col) => (
                <th
                  key={col.field}
                  className={clsx(
                    'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200',
                    col.sortable !== false && 'cursor-pointer hover:bg-gray-100'
                  )}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => col.sortable !== false && handleSort(col.field)}
                >
                  <div className="flex items-center gap-1">
                    <span>{col.header}</span>
                    {col.sortable !== false && <SortIcon column={col.field} />}
                  </div>
                </th>
              ))}
            </tr>

            {/* Filter Row - Incremental Search (like Infor LN) */}
            {showColumnFilters && (
              <tr className="bg-gray-100 border-b border-gray-300">
                {/* Expand column filter placeholder */}
                {expandable?.enabled && (
                  <th className="w-10 px-2 py-1 bg-gray-100">
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear all filters"
                      >
                        <FilterX className="w-4 h-4" />
                      </button>
                    )}
                  </th>
                )}
                {data.columns.map((col) => {
                  const isFilterable = col.filterable !== false;
                  const currentOperator = columnFilters[col.field]?.operator || getDefaultOperator(col);
                  const inputValue = filterInputs[col.field] ?? columnFilters[col.field]?.value ?? '';
                  const hasValue = inputValue.trim() !== '';

                  if (!isFilterable) {
                    return <th key={`filter-${col.field}`} className="px-2 py-1 bg-gray-100" />;
                  }

                  return (
                    <th key={`filter-${col.field}`} className="px-2 py-1 bg-gray-100">
                      <div className="flex items-center gap-1">
                        {/* Operator selector button */}
                        <OperatorSelector
                          operator={currentOperator}
                          columnType={col.format?.type || 'text'}
                          onChange={(op) => handleOperatorChange(col.field, op)}
                        />

                        {/* Filter input */}
                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => handleFilterInputChange(col.field, e.target.value, col)}
                          placeholder=""
                          className={clsx(
                            'flex-1 min-w-0 px-2 py-1 text-xs border rounded',
                            'focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]',
                            hasValue ? 'border-[#45bdd6] bg-blue-50' : 'border-gray-300 bg-white'
                          )}
                        />

                        {/* Clear button */}
                        {hasValue && (
                          <button
                            onClick={() => clearFilter(col.field)}
                            className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                            title="Clear filter"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {paginatedRows.map((row, rowIdx) => {
              const isExpanded = expandedRows.has(rowIdx);
              const canExpand = hasExpandableData(row);
              const nestedCount = getNestedDataCount(row);

              return (
                <React.Fragment key={`row-fragment-${rowIdx}`}>
                  {/* Main data row */}
                  <tr
                    className={clsx(
                      'border-b border-gray-100',
                      striped && rowIdx % 2 === 1 && 'bg-gray-50',
                      onRowClick && 'cursor-pointer hover:bg-blue-50',
                      isExpanded && 'bg-blue-50'
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {/* Expand button cell */}
                    {expandable?.enabled && (
                      <td className="w-10 px-2 py-3 text-center">
                        {canExpand && (
                          <button
                            onClick={(e) => toggleRowExpansion(rowIdx, e)}
                            className={clsx(
                              'p-1 rounded transition-all duration-200',
                              'hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#45bdd6]',
                              isExpanded && 'bg-[#45bdd6] text-white hover:bg-[#3ba8bf]'
                            )}
                            title={isExpanded ? 'Collapse' : `Show ${nestedCount} ${expandable.title || 'entries'}`}
                          >
                            <ChevronRightIcon
                              className={clsx(
                                'w-4 h-4 transition-transform duration-200',
                                isExpanded && 'rotate-90'
                              )}
                            />
                          </button>
                        )}
                        {canExpand && expandable.showCount && (
                          <span className="ml-1 text-xs text-gray-500">
                            ({nestedCount})
                          </span>
                        )}
                      </td>
                    )}
                    {data.columns.map((col) => {
                      const cellValue = row[col.field];
                      const condStyle = getConditionalStyle(cellValue, col.conditionalFormats);
                      const formattedValue = formatCellValue(cellValue, col.format);

                      return (
                        <td
                          key={col.field}
                          className={clsx(
                            'px-4 py-3 text-sm truncate max-w-xs',
                            !condStyle && 'text-gray-900'
                          )}
                          style={{
                            backgroundColor: condStyle?.backgroundColor,
                            color: condStyle?.textColor,
                            fontWeight: condStyle?.fontWeight,
                          }}
                          title={String(cellValue ?? '')}
                        >
                          {condStyle?.icon && (
                            <ConditionalIcon
                              iconName={condStyle.icon}
                              color={condStyle.iconColor}
                            />
                          )}
                          {formattedValue}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded nested data row */}
                  {expandable?.enabled && isExpanded && canExpand && (
                    <tr className="bg-gray-50">
                      <td colSpan={totalColumns} className="p-0">
                        <NestedDataTable
                          data={row[expandable.dataField] as Record<string, unknown>[]}
                          columns={expandable.columns}
                          title={expandable.title}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
          <div className="text-sm text-gray-500">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, sortedRows.length)} of {sortedRows.length} results
            {hasActiveFilters && filteredRows.length !== data.rows.length && (
              <span className="ml-2 text-[#45bdd6]">
                (filtered from {data.rows.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Format a cell value based on column format settings (like Excel)
 */
function formatCellValue(value: unknown, format?: ColumnFormat): string {
  if (value === null || value === undefined) {
    return format?.nullValue ?? '—';
  }

  if (!format) {
    // Default formatting
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  const { type, decimals, prefix, suffix, dateFormat, thousandsSeparator } = format;

  switch (type) {
    case 'number': {
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      if (isNaN(num)) return String(value);
      let formatted = decimals !== undefined ? num.toFixed(decimals) : String(num);
      if (thousandsSeparator) {
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        formatted = parts.join(',');
      }
      return `${prefix || ''}${formatted}${suffix || ''}`;
    }

    case 'currency': {
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      if (isNaN(num)) return String(value);
      const formatted = num.toLocaleString('en-GB', {
        minimumFractionDigits: decimals ?? 2,
        maximumFractionDigits: decimals ?? 2,
      });
      return `${prefix || '£ '}${formatted}${suffix || ''}`;
    }

    case 'percent': {
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      if (isNaN(num)) return String(value);
      const percentage = num * 100;
      const formatted = decimals !== undefined ? percentage.toFixed(decimals) : String(percentage);
      return `${prefix || ''}${formatted}${suffix || ' %'}`;
    }

    case 'date': {
      const date = value instanceof Date ? value : new Date(String(value));
      if (isNaN(date.getTime())) return String(value);
      const formatStr = dateFormat || 'dd.MM.yyyy';
      return formatDateString(date, formatStr);
    }

    case 'datetime': {
      const date = value instanceof Date ? value : new Date(String(value));
      if (isNaN(date.getTime())) return String(value);
      const formatStr = dateFormat || 'dd.MM.yyyy HH:mm';
      return formatDateString(date, formatStr);
    }

    case 'boolean': {
      const boolVal = typeof value === 'boolean' ? value : String(value).toLowerCase() === 'true';
      return boolVal ? 'Yes' : 'No';
    }

    case 'text':
    default:
      return `${prefix || ''}${String(value)}${suffix || ''}`;
  }
}

/**
 * Simple date formatter
 */
function formatDateString(date: Date, format: string): string {
  const pad = (n: number) => n.toString().padStart(2, '0');

  return format
    .replace('yyyy', date.getFullYear().toString())
    .replace('yy', date.getFullYear().toString().slice(-2))
    .replace('MM', pad(date.getMonth() + 1))
    .replace('dd', pad(date.getDate()))
    .replace('HH', pad(date.getHours()))
    .replace('mm', pad(date.getMinutes()))
    .replace('ss', pad(date.getSeconds()));
}

/**
 * Evaluate dynamic variables like today(), now(), etc.
 * Returns the resolved value or the original value if not a variable
 */
function evaluateVariable(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();

  // Check if it's a variable pattern
  const variableMatch = trimmed.match(/^([a-zA-Z]+)\(([^)]*)\)$/);
  if (!variableMatch) return value;

  const [, funcName, arg] = variableMatch;
  const now = new Date();

  switch (funcName.toLowerCase()) {
    case 'today': {
      // Start of today (00:00:00)
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return today;
    }

    case 'now': {
      return now;
    }

    case 'yesterday': {
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      return yesterday;
    }

    case 'tomorrow': {
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      return tomorrow;
    }

    case 'startofweek': {
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday is start of week
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
      return startOfWeek;
    }

    case 'endofweek': {
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek; // Sunday is end of week
      const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
      return endOfWeek;
    }

    case 'startofmonth': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return startOfMonth;
    }

    case 'endofmonth': {
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return endOfMonth;
    }

    case 'daysago': {
      const days = parseInt(arg, 10);
      if (!isNaN(days)) {
        const daysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
        return daysAgo;
      }
      return value;
    }

    case 'daysfromnow': {
      const days = parseInt(arg, 10);
      if (!isNaN(days)) {
        const daysFromNow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
        return daysFromNow;
      }
      return value;
    }

    default:
      return value;
  }
}

/**
 * Compare two values, handling dates properly
 */
function compareValues(a: unknown, b: unknown, operator: string): boolean {
  // If either is a Date, try to compare as dates
  const aDate = a instanceof Date ? a : (typeof a === 'string' ? new Date(a) : null);
  const bDate = b instanceof Date ? b : (typeof b === 'string' ? new Date(b) : null);

  if (aDate && !isNaN(aDate.getTime()) && bDate && !isNaN(bDate.getTime())) {
    const aTime = aDate.getTime();
    const bTime = bDate.getTime();

    switch (operator) {
      case 'equals':
        return aTime === bTime;
      case 'not_equals':
        return aTime !== bTime;
      case 'greater':
        return aTime > bTime;
      case 'greater_equal':
        return aTime >= bTime;
      case 'less':
        return aTime < bTime;
      case 'less_equal':
        return aTime <= bTime;
      default:
        return false;
    }
  }

  // Numeric comparison
  const aNum = typeof a === 'number' ? a : parseFloat(String(a));
  const bNum = typeof b === 'number' ? b : parseFloat(String(b));

  if (!isNaN(aNum) && !isNaN(bNum)) {
    switch (operator) {
      case 'equals':
        return aNum === bNum;
      case 'not_equals':
        return aNum !== bNum;
      case 'greater':
        return aNum > bNum;
      case 'greater_equal':
        return aNum >= bNum;
      case 'less':
        return aNum < bNum;
      case 'less_equal':
        return aNum <= bNum;
      default:
        return false;
    }
  }

  // String comparison
  const aStr = String(a ?? '').toLowerCase();
  const bStr = String(b ?? '').toLowerCase();

  switch (operator) {
    case 'equals':
      return aStr === bStr;
    case 'not_equals':
      return aStr !== bStr;
    default:
      return false;
  }
}

/**
 * Evaluate a conditional format rule against a cell value
 */
function evaluateCondition(value: unknown, condition: ConditionalFormat['condition']): boolean {
  const { operator, value: condValue, value2 } = condition;

  // Handle empty checks first
  if (operator === 'is_empty') {
    return value === null || value === undefined || value === '';
  }
  if (operator === 'is_not_empty') {
    return value !== null && value !== undefined && value !== '';
  }

  // Evaluate variables in condition values
  const resolvedCondValue = evaluateVariable(condValue);
  const resolvedValue2 = value2 !== undefined ? evaluateVariable(value2) : undefined;

  // Convert values for comparison
  const strValue = String(value ?? '').toLowerCase();
  const strCondValue = String(resolvedCondValue ?? '').toLowerCase();

  // String operations
  if (operator === 'contains') {
    return strValue.includes(strCondValue);
  }
  if (operator === 'starts_with') {
    return strValue.startsWith(strCondValue);
  }
  if (operator === 'ends_with') {
    return strValue.endsWith(strCondValue);
  }

  // Use compareValues for comparison operators (handles dates and numbers)
  if (['equals', 'not_equals', 'greater', 'greater_equal', 'less', 'less_equal'].includes(operator)) {
    const result = compareValues(value, resolvedCondValue, operator);

    // Handle 'between' as a special case
    if (operator === 'between' && resolvedValue2 !== undefined) {
      return compareValues(value, resolvedCondValue, 'greater_equal') &&
             compareValues(value, resolvedValue2, 'less_equal');
    }

    return result;
  }

  // Handle 'between' operator
  if (operator === 'between' && resolvedValue2 !== undefined) {
    return compareValues(value, resolvedCondValue, 'greater_equal') &&
           compareValues(value, resolvedValue2, 'less_equal');
  }

  return false;
}

/**
 * Get the matching conditional format style for a cell value
 */
function getConditionalStyle(
  value: unknown,
  conditionalFormats?: ConditionalFormat[]
): ConditionalFormatStyle | null {
  if (!conditionalFormats || conditionalFormats.length === 0) return null;

  // Find the first matching rule (order matters)
  for (const format of conditionalFormats) {
    if (evaluateCondition(value, format.condition)) {
      return format.style;
    }
  }

  return null;
}

/**
 * Render conditional format icon
 */
function ConditionalIcon({ iconName, color }: { iconName?: string; color?: string }) {
  if (!iconName) return null;

  const IconComponent = iconMap[iconName];
  if (!IconComponent) return null;

  return (
    <span style={{ color: color || 'currentColor' }} className="inline-flex items-center mr-1">
      <IconComponent className="w-4 h-4" />
    </span>
  );
}

/**
 * Operator Selector Component
 * Dropdown to select filter operator (like [A], [=], [≥] in Infor LN)
 */
interface OperatorSelectorProps {
  operator: FilterOperator;
  columnType: string;
  onChange: (operator: FilterOperator) => void;
}

function OperatorSelector({ operator, columnType, onChange }: OperatorSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Available operators based on column type
  const availableOperators = useMemo((): FilterOperator[] => {
    if (columnType === 'number' || columnType === 'currency' || columnType === 'percent') {
      return ['equals', 'not_equals', 'greater', 'greater_equal', 'less', 'less_equal'];
    }
    if (columnType === 'date' || columnType === 'datetime') {
      return ['equals', 'not_equals', 'greater', 'greater_equal', 'less', 'less_equal'];
    }
    if (columnType === 'boolean') {
      return ['equals', 'not_equals'];
    }
    // Text type
    return ['contains', 'starts_with', 'equals', 'not_equals'];
  }, [columnType]);

  const label = FILTER_OPERATOR_LABELS[operator] || 'A';

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'px-1.5 py-1 text-xs font-mono border rounded min-w-[28px]',
          'hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-[#45bdd6]',
          'transition-colors duration-150',
          isOpen ? 'bg-[#45bdd6] text-white border-[#45bdd6]' : 'bg-white border-gray-300 text-gray-600'
        )}
        title={`Filter operator: ${operator}`}
      >
        {label}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-300 rounded shadow-lg min-w-[120px]">
          {availableOperators.map((op) => (
            <button
              key={op}
              onClick={() => {
                onChange(op);
                setIsOpen(false);
              }}
              className={clsx(
                'w-full px-3 py-1.5 text-left text-xs flex items-center gap-2',
                'hover:bg-gray-100 transition-colors',
                operator === op && 'bg-[#45bdd6]/10 text-[#2b5a83] font-medium'
              )}
            >
              <span className="font-mono w-6">[{FILTER_OPERATOR_LABELS[op]}]</span>
              <span className="text-gray-600">{getOperatorLabel(op)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Get human-readable label for filter operator
 */
function getOperatorLabel(operator: FilterOperator): string {
  switch (operator) {
    case 'contains': return 'Contains';
    case 'starts_with': return 'Starts with';
    case 'equals': return 'Equals';
    case 'not_equals': return 'Not equals';
    case 'greater': return 'Greater than';
    case 'greater_equal': return 'Greater or equal';
    case 'less': return 'Less than';
    case 'less_equal': return 'Less or equal';
    default: return operator;
  }
}

/**
 * Nested Data Table Component
 * Renders the sub-table for expanded rows (e.g., OrderLines, Operations)
 */
interface NestedDataTableProps {
  data: Record<string, unknown>[];
  columns: TableColumn[];
  title?: string;
}

function NestedDataTable({ data, columns, title }: NestedDataTableProps) {
  if (!data || data.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500 italic">
        No detail data available
      </div>
    );
  }

  return (
    <div className="border-l-4 border-[#45bdd6] ml-4 my-2">
      {/* Header with title and count */}
      <div className="px-4 py-2 bg-gradient-to-r from-[#45bdd6]/10 to-transparent">
        <span className="text-sm font-medium text-[#2b5a83]">
          {title || 'Detail Data'}
        </span>
        <span className="ml-2 px-2 py-0.5 text-xs bg-[#45bdd6] text-white rounded-full">
          {data.length} {data.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Nested table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr
                key={idx}
                className={clsx(
                  'border-b border-gray-100',
                  idx % 2 === 1 && 'bg-gray-50/50'
                )}
              >
                {columns.map((col) => {
                  const cellValue = item[col.field];
                  const condStyle = getConditionalStyle(cellValue, col.conditionalFormats);
                  const formattedValue = formatCellValue(cellValue, col.format);

                  return (
                    <td
                      key={col.field}
                      className={clsx(
                        'px-3 py-2 text-sm truncate max-w-xs',
                        !condStyle && 'text-gray-700'
                      )}
                      style={{
                        backgroundColor: condStyle?.backgroundColor,
                        color: condStyle?.textColor,
                        fontWeight: condStyle?.fontWeight,
                      }}
                      title={String(cellValue ?? '')}
                    >
                      {condStyle?.icon && (
                        <ConditionalIcon
                          iconName={condStyle.icon}
                          color={condStyle.iconColor}
                        />
                      )}
                      {formattedValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
